// ============================================
// Creator Store (Zustand)
// 캐릭터 크리에이터 상태 관리
// ============================================

import { createStore } from 'zustand/vanilla';

export const MAX_IMAGES = 5;
export const MAX_SITUATIONAL_IMAGES = 100;
export const MAX_LOREBOOK_ENTRIES = 100;
export const GALLERY_PAGE_SIZE = 20;
export const INTRO_IMAGE_PAGE_SIZE = 20;
const CACHE_TTL = 5 * 60 * 1000; // 5분

export const useCreatorStore = createStore((set, get) => ({
  // ============================================
  // Creations & Stats
  // ============================================
  currentCreations: [],
  currentFilter: 'all',
  currentSort: 'recent',
  isLoadingCreations: false,
  currentStatsCreationId: null,
  currentStatsPeriod: 7,

  setCurrentCreations: (creations) => set({ currentCreations: creations }),
  setCurrentFilter: (filter) => set({ currentFilter: filter }),
  setCurrentSort: (sort) => set({ currentSort: sort }),
  setIsLoadingCreations: (loading) => set({ isLoadingCreations: loading }),
  setCurrentStatsCreationId: (id) => set({ currentStatsCreationId: id }),
  setCurrentStatsPeriod: (period) => set({ currentStatsPeriod: period }),

  // ============================================
  // Editor
  // ============================================
  currentEditorData: {
    id: null,
    status: null,
    images: [],
    generatedImages: [],
    selectedImageIndex: null
  },
  initialEditorState: null,

  resetEditorData: () => set({
    currentEditorData: {
      id: null,
      status: null,
      images: [],
      generatedImages: [],
      selectedImageIndex: null
    }
  }),
  setEditorDataId: (id) => set((state) => ({
    currentEditorData: { ...state.currentEditorData, id }
  })),
  setEditorDataStatus: (status) => set((state) => ({
    currentEditorData: { ...state.currentEditorData, status }
  })),
  setCurrentEditorData: (data) => set({ currentEditorData: data }),
  setInitialEditorState: (state) => set({ initialEditorState: state }),

  // ============================================
  // Cache
  // ============================================
  cache: {
    generatedImages: { data: null, timestamp: 0 },
    imageModels: { data: null, timestamp: 0 },
    creations: { data: null, timestamp: 0 }
  },

  isCacheValid: (cacheKey) => {
    const cached = get().cache[cacheKey];
    return cached?.data !== null && (Date.now() - cached.timestamp) < CACHE_TTL;
  },
  setCache: (cacheKey, data) => set((state) => ({
    cache: {
      ...state.cache,
      [cacheKey]: { data, timestamp: Date.now() }
    }
  })),
  invalidateCache: (cacheKey) => set((state) => {
    if (cacheKey) {
      return {
        cache: {
          ...state.cache,
          [cacheKey]: { data: null, timestamp: 0 }
        }
      };
    }
    return {
      cache: {
        generatedImages: { data: null, timestamp: 0 },
        imageModels: { data: null, timestamp: 0 },
        creations: { data: null, timestamp: 0 }
      }
    };
  }),

  // ============================================
  // Intro Blocks
  // ============================================
  introBlocks: [],
  currentIntroType: 'narration',
  selectedIntroBlockIndex: null,
  introImagePage: 0,
  introImageLoading: false,
  introImageHasMore: true,
  introImageMode: 'insert',
  editingIntroImageIndex: null,

  setIntroBlocks: (blocks) => set({ introBlocks: blocks }),
  setCurrentIntroType: (type) => set({ currentIntroType: type }),
  setSelectedIntroBlockIndex: (index) => set({ selectedIntroBlockIndex: index }),
  setIntroImagePage: (page) => set({ introImagePage: page }),
  setIntroImageLoading: (loading) => set({ introImageLoading: loading }),
  setIntroImageHasMore: (hasMore) => set({ introImageHasMore: hasMore }),
  setIntroImageMode: (mode) => set({ introImageMode: mode }),
  setEditingIntroImageIndex: (index) => set({ editingIntroImageIndex: index }),

  // ============================================
  // Example Dialogues (대화 예시)
  // ============================================
  exampleDialogues: [],
  currentExampleType: 'dialogue',
  editingExampleIndex: null,

  setExampleDialogues: (dialogues) => set({ exampleDialogues: dialogues }),
  addExampleDialogue: (dialogue) => set((state) => ({
    exampleDialogues: [...state.exampleDialogues, dialogue]
  })),
  updateExampleDialogue: (index, dialogue) => set((state) => ({
    exampleDialogues: state.exampleDialogues.map((d, i) => i === index ? dialogue : d)
  })),
  removeExampleDialogue: (index) => set((state) => ({
    exampleDialogues: state.exampleDialogues.filter((_, i) => i !== index)
  })),
  setCurrentExampleType: (type) => set({ currentExampleType: type }),
  setEditingExampleIndex: (index) => set({ editingExampleIndex: index }),

  // ============================================
  // Situational Images (상황별 이미지)
  // ============================================
  situationalImages: [],
  editingSituationalIndex: null,

  setSituationalImages: (images) => set({ situationalImages: images }),
  addSituationalImage: (image) => set((state) => ({
    situationalImages: [...state.situationalImages, image]
  })),
  updateSituationalImage: (index, image) => set((state) => ({
    situationalImages: state.situationalImages.map((img, i) => i === index ? image : img)
  })),
  removeSituationalImage: (index) => set((state) => ({
    situationalImages: state.situationalImages.filter((_, i) => i !== index)
  })),
  setEditingSituationalIndex: (index) => set({ editingSituationalIndex: index }),

  // ============================================
  // Lorebook (로어북)
  // ============================================
  lorebookEntries: [],
  editingLorebookIndex: null,

  setLorebookEntries: (entries) => set({ lorebookEntries: entries }),
  addLorebookEntry: (entry) => set((state) => ({
    lorebookEntries: [...state.lorebookEntries, entry]
  })),
  updateLorebookEntry: (index, entry) => set((state) => ({
    lorebookEntries: state.lorebookEntries.map((e, i) => i === index ? entry : e)
  })),
  removeLorebookEntry: (index) => set((state) => ({
    lorebookEntries: state.lorebookEntries.filter((_, i) => i !== index)
  })),
  setEditingLorebookIndex: (index) => set({ editingLorebookIndex: index }),

  // ============================================
  // Gallery
  // ============================================
  galleryImagePage: 0,
  galleryImageLoading: false,
  galleryImageHasMore: true,
  imageModelList: [],

  setGalleryImagePage: (page) => set({ galleryImagePage: page }),
  setGalleryImageLoading: (loading) => set({ galleryImageLoading: loading }),
  setGalleryImageHasMore: (hasMore) => set({ galleryImageHasMore: hasMore }),
  setImageModelList: (list) => set({ imageModelList: list }),

  // ============================================
  // Autofill
  // ============================================
  autofillTagsCache: null,
  fullGenSelectedGenres: [],
  fullGenSelectedModelId: '',
  loadingMessageInterval: null,
  searchTimeout: null,

  setAutofillTagsCache: (tags) => set({ autofillTagsCache: tags }),
  setFullGenSelectedGenres: (genres) => set({ fullGenSelectedGenres: genres }),
  setFullGenSelectedModelId: (modelId) => set({ fullGenSelectedModelId: modelId }),
  setLoadingMessageInterval: (interval) => set({ loadingMessageInterval: interval }),
  setSearchTimeout: (timeout) => set({ searchTimeout: timeout })
}));

export default useCreatorStore;
